// src/services/drivers/driversService.js
import axios from '../axiosInstance';
import qs from 'qs';

/**
 * DataGrid-style query for enabled drivers (keyset pagination, sort, filter).
 * Backend: POST api/Drivers/queryEnabled
 * Returns: { items, cursor, hasMore }
 */
export async function queryEnabledDrivers({
    search,
    pageSize,
    cursor,
    sortBy,
    sortDir,
    filterModel,
    signal = null,
} = {}) {
    const body = {
        search: search ?? null,
        pageSize,
        cursor: cursor ?? null,
        sortBy: sortBy ?? 'name',
        sortDir: sortDir ?? 'asc',
        filterModel: filterModel ?? { items: [], logicOperator: 'and' },
    };

    const { data } = await axios.post('/Drivers/queryEnabled', body, { signal });
    return data;
}

// GET enabled, paginated (kept for route loader / lookups). Returns { items, lastId, hasMore }
export async function getDriversEnabled({
    search = '',
    pageSize = 50,
    lastId = null,
    transTypeId = null,
    haulierIds = null,
    searchFields = null,
    signal = null,
} = {}) {
    const params = new URLSearchParams();
    if (search != null) params.set('search', search);
    if (pageSize != null) params.set('pageSize', String(pageSize));
    if (lastId != null) params.set('lastId', String(lastId));
    if (transTypeId != null) params.set('transTypeId', String(transTypeId));
    if (Array.isArray(haulierIds) && haulierIds.length) {
        haulierIds.forEach(id => params.append('haulierIds', String(id)));
    }
    if (Array.isArray(searchFields) && searchFields.length) {
        searchFields.forEach((f) => params.append('searchFields', f));
    }
    const { data } = await axios.get('/Drivers/getEnabled', { params, signal });
    return data;
}

// GET by id
export async function getDriverById(id, signal = null) {
    const { data } = await axios.get(`/Drivers/${id}`, { signal });
    return data;
}

// POST create
export async function createDriver(payload, signal = null) {
    const { data } = await axios.post('/Drivers', payload, { signal });
    return data;
}

// PUT update
export async function updateDriver(id, payload, signal = null) {
    await axios.put(`/Drivers/${id}`, payload, { signal });
    return true;
}

export async function deleteDriver(id, signal = null) {
    const { data } = await axios.delete(`/Drivers/${id}`, { signal });
    return data;
}

export async function restoreDriver(id, signal = null) {
    await axios.post(`/Drivers/${id}/restore`, null, { signal });
    return true;
}

/**
 * Fetch enabled drivers by their IDs (for PPD filtering).
 * @param {number[]} ids - Array of driver IDs to fetch
 * @returns {Promise<{items: Array}>} Promise resolving to drivers data
 */
export async function getEnabledDriversByIds(ids, signal = null) {
    if (!ids || ids.length === 0) return { items: [] };
    const res = await axios.post(
        "/Drivers/byIdsEnabled",
        { filterModel: { items: [], logicOperator: 'and' } },
        {
            params: { ids },
            paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
            signal,
        }
    );
    return res.data;
}

/**
 * Fetch lightweight driver data by IDs (only id + display fields for weighing console).
 * Returns minimal data: id, name, erpCode, haulier (with name) for display purposes.
 * @param {number[]} ids - Array of driver IDs to fetch
 * @returns {Promise<Array>} Promise resolving to array of lightweight driver objects
 */
export async function getEnabledDriversByIdsLightweight(ids, signal = null) {
    if (!ids || ids.length === 0) return [];
    const res = await axios.post(
        "/Drivers/byIdsWeighingConsole",
        ids,
        { signal }
    );
    return res.data; // Returns array directly: [{ id, name, erpCode, haulier: { name } }]
}
